import React, { useEffect, useState } from 'react';
import {
  TableColumnDefinition,
  createTableColumn,
  Divider,
  TableCellLayout,
  Checkbox,
  mergeClasses,
  Image,
  Spinner,
} from '@fluentui/react-components';
import {
  PageSizeSelector,
  Paginator, SearchBox, TableGridView,
  useRenderingData,
} from '@bingads-webui-fluent-component/entity-selector';
import { SortDefinition, useDataSource } from '@bingads-webui-react/data-source';

import { ItemGoalType } from './types';
import { useTableStyles } from './table-view-section-styles';
import { categoryMapping, EMPTY_STATE, Scopes } from './constants';
import { TrackingStatusPopover } from './components/tracking-status-popover';
import { EmptyStateResultComponent } from './components/empty-state-result';
import { HeaderControlComponent } from './components/header-control';
import OtherIcon from '../media/other.svg';

export const TableViewSection: React.FC<any> = ({
  i18n, childrenProps, customFilter, scope, isCreationWorkflow, activityLogger, setScope, ...resetProps
}) => {
  const {
    sort, searchTerm, isLoading, count,
  } = useDataSource();
  const showSelectedOnly = false;
  const classes = useTableStyles();
  const [showNoMainGoalWarning, setShowNoMainGoalWarning] = useState(false);
  const [sortState, setSortState] = useState(undefined);
  const isAccountLevelScope = scope === Scopes.ACCOUNT;

  useEffect(() => {
    if (count === 0 && searchTerm === '' && !isLoading && isAccountLevelScope) {
      setShowNoMainGoalWarning(true);
    } else {
      setShowNoMainGoalWarning(false);
    }
  }, [count, isLoading, isAccountLevelScope, searchTerm]);

  const columns: TableColumnDefinition<ItemGoalType>[] = [
    createTableColumn<ItemGoalType>({
      columnId: 'Name',
      renderHeaderCell: () => <span className={classes.headerTitle}>{i18n.getString(_TL_('Conversion goal'))}</span>,
      renderCell: item => (
        <TableCellLayout media={<Image src={categoryMapping[item.data.GoalCategory] ? categoryMapping[item.data.GoalCategory].icon : OtherIcon} />}>
          {`${item.data.Name} ${item.data.IsMainConversionGoal ? i18n.getString(_TL_('(Account default)')) : ''}`}
        </TableCellLayout>
      ),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      compare: (a, b) => 1, // enable sorting
    }),
    createTableColumn<ItemGoalType>({
      columnId: 'GoalCategory',
      renderHeaderCell: () => <span className={classes.headerTitle}>{i18n.getString(_TL_('Category'))}</span>,
      renderCell: item => (categoryMapping[item.data.GoalCategory] ? categoryMapping[item.data.GoalCategory].text : i18n.getString(_TL_('Unassigned'))),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      compare: (a, b) => 1, // enable sorting
    }),
    createTableColumn<ItemGoalType>({
      columnId: 'TrackingStatus',
      renderHeaderCell: () => <span className={classes.headerTitle}>{i18n.getString(_TL_('Tracking Status'))}</span>,
      renderCell: item => (
        <TrackingStatusPopover i18n={i18n} goal={item.data} isCreationWorkflow={isCreationWorkflow} activityLogger={activityLogger} />
      ),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      compare: (a, b) => 1, // enable sorting
    }),
  ];
  const {
    items,
  } = useRenderingData({
    mode: 'page',
    showSelectedOnly,
    customFilter,
  });

  const isSearchEmptyState = items && items.length === 0 && searchTerm;
  const renderingColumns = isAccountLevelScope ? [createTableColumn<ItemGoalType>({
    columnId: 'IsMainConversionGoal',
    renderHeaderCell: () => <div className={classes.mainConversionCheckbox} />,
    renderCell: item => (
      <Checkbox
        disabled
        className={classes.mainConversionCheckbox}
        checked={item.data.IsMainConversionGoal}
        aria-label={i18n.getString(_TL_('Count as “conversions” is true'))}
      />
    ),
  }), ...columns] : columns;

  const onHandleSort = (_ev, val) => {
    setSortState(val);
    if (activityLogger && activityLogger.trace && val) {
      activityLogger.trace(`[GoalSelectorV3] sort: ${JSON.stringify(val)}`);
    }
    sort((val as SortDefinition));
  };

  return (
    <>
      <HeaderControlComponent i18n={i18n} scope={scope} setScope={setScope} showNoMainGoalWarning={showNoMainGoalWarning} />
      {!showNoMainGoalWarning && (
        <div className={mergeClasses(classes.dataGridWrapper, isAccountLevelScope && classes.accountCheckbox)}>
          <div className={classes.headerBar}>
            <SearchBox
              className={classes.searchbox}
              appearance="filled-darker"
              placeholder={i18n.getString(_TL_('Search conversion goal name'))}
            />
          </div>
          <div className={classes.dataGridContainer}>
            {isLoading && (
            <div className="loading-goal" ><Spinner appearance="primary" size="extra-small" label={i18n.getString(_TL_('Loading...'))} /> </div>
              )
            }
            {!isLoading && <TableGridView
              i18n={i18n}
              aggregateMode="page"
              classes={classes}
              columns={renderingColumns}
              customFilter={customFilter}
              showSelectedOnly={showSelectedOnly}
              showClearAllHeader={!isAccountLevelScope}
              disabledToggleWholeRow
              disableClientSort
              showDefaultSelectionHeader={false}
              appearanceRow={isAccountLevelScope ? 'none' : 'brand'}
              additionalDataGridProps={{
                sortState,
                onSortChange: onHandleSort,
                'aria-label': i18n.getString(_TL_('Conversion Goal Selector Gird view')),
              }}
              {...childrenProps}
              {...resetProps}
              selectionMode={isAccountLevelScope ? false : 'multiselect'}
            />}
          </div>
          { isSearchEmptyState && !isLoading &&
          <EmptyStateResultComponent i18n={i18n} emptyState={EMPTY_STATE.SearchEmpty} isCreationWorkflow={isCreationWorkflow} activityLogger={activityLogger} />
        }
          { isSearchEmptyState && !isLoading ? <></> :
          <>
            <Divider />
            <div className={classes.footer}>
              <PageSizeSelector pageSizeOptions={[25, 30, 50, 100]} i18n={i18n} />
              <Paginator i18n={i18n} />
            </div>
          </>
      }
        </div>
      )}
    </>
  );
};
